import React, { useEffect, useMemo, useRef, useState } from "react";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const DEFAULT_ANIMATION_DURATION = 2000;
const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);

const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event) => setPrefersReducedMotion(event.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return prefersReducedMotion;
};

const formatScore = (value) => `${Math.round(value)}`;

const CircularProgressBar = ({
  progress,
  size = 100,
  strokeWidth = 10,
  color = "#1C4ED8",
  backgroundColor = "#C8D8FF",
  animateOnLoad = true,
  animationDuration = DEFAULT_ANIMATION_DURATION,
  ariaLabel,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const hasAnimatedOnMountRef = useRef(false);
  const safeProgress = clamp(Number(progress) || 0, 0, 100);
  const safeDuration = Math.max(0, Number(animationDuration) || 0);
  const [animatedProgress, setAnimatedProgress] = useState(
    animateOnLoad ? 0 : safeProgress,
  );
  const animatedProgressRef = useRef(animatedProgress);

  useEffect(() => {
    animatedProgressRef.current = animatedProgress;
  }, [animatedProgress]);

  useEffect(() => {
    if (prefersReducedMotion || !animateOnLoad) {
      hasAnimatedOnMountRef.current = true;
      setAnimatedProgress(safeProgress);
      return undefined;
    }

    const startValue = hasAnimatedOnMountRef.current
      ? animatedProgressRef.current
      : 0;
    const delta = safeProgress - startValue;

    if (delta === 0) {
      hasAnimatedOnMountRef.current = true;
      return undefined;
    }

    if (safeDuration === 0) {
      hasAnimatedOnMountRef.current = true;
      setAnimatedProgress(safeProgress);
      return undefined;
    }

    hasAnimatedOnMountRef.current = true;
    let frameId;
    let startTime;

    const animate = (time) => {
      if (!startTime) startTime = time;
      const elapsed = time - startTime;
      const progressRatio = Math.min(elapsed / safeDuration, 1);
      const easedProgress = easeOutCubic(progressRatio);
      const nextValue = startValue + delta * easedProgress;

      setAnimatedProgress(progressRatio === 1 ? safeProgress : nextValue);

      if (progressRatio < 1) {
        frameId = requestAnimationFrame(animate);
      }
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [animateOnLoad, prefersReducedMotion, safeDuration, safeProgress]);

  const radius = useMemo(() => (size - strokeWidth) / 2, [size, strokeWidth]);
  const circumference = useMemo(() => 2 * Math.PI * radius, [radius]);
  const offset = useMemo(
    () => circumference - (animatedProgress / 100) * circumference,
    [animatedProgress, circumference],
  );
  const scoreValue = animatedProgress;
  const svgLabel = ariaLabel ?? `Progress: ${formatScore(safeProgress)}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={svgLabel}
    >
      {/* Background Circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={backgroundColor}
        strokeWidth={strokeWidth}
        fill="none"
      />
      {/* Progress Circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="middle"
        textAnchor="middle"
        fontSize={size * 0.3}
        fontWeight="normal"
        fill={color}
      >
        {formatScore(scoreValue)}
      </text>
    </svg>
  );
};

export default CircularProgressBar;
